import * as React from "react";
import get from "lodash/get";

import { CircularProgress, makeStyles, Typography } from "@material-ui/core";
import classnames from "classnames";

import { useRecordContext } from "ra-core";
import { sanitizeFieldRestProps } from "ra-ui-materialui";

import { getPhotoUrl } from "../../utils/fileUtils";
import { PhotoStatus } from "../../utils/constants";

const useStyles = makeStyles(
  (theme) => ({
    list: {
      display: "flex",
      listStyleType: "none",
    },
    image: {
      margin: "8px",
      maxWidth: (props) =>
        props.scaleThumb ? `${props.thumbWidth}px` : "none",
      maxHeight: (props) =>
        props.scaleThumb ? `${props.thumbHeight}px` : "none",
      width: (props) => (props.scaleThumb ? "auto" : "initial"),
      height: (props) => (props.scaleThumb ? "auto" : "initial"),
    },
    spinnerContainer: {
      position: "absolute",
      top: "50%",
      left: "50%",
      transform: "translate(-50%, -50%)",
      pointerEvents: "none",
    },
  }),
  { name: "RaImageField" }
);

export const ImageField = ({
  thumbWidth = 512,
  thumbHeight = 512,
  scaleThumb = false,
  fullSize = 1024,
  ...props
}) => {
  const {
    className,
    classes: classesOverride,
    emptyText,
    source,
    src,
    title,
    ...rest
  } = props;
  const record = useRecordContext(props);
  const classes = useStyles({ thumbWidth, thumbHeight, scaleThumb });
  let imgSrc = get(record, source);
  let fullSizeSrc = null;
  const status = record?.status;

  const getKeySource = (record) => {
    const keyValue = get(record, "key");
    if (!keyValue) {
      return emptyText ? (
        <Typography
          component="span"
          variant="body2"
          className={className}
          {...sanitizeFieldRestProps(rest)}
        >
          {emptyText}
        </Typography>
      ) : (
        <div className={className} {...sanitizeFieldRestProps(rest)} />
      );
    } else {
      imgSrc = getPhotoUrl(keyValue, thumbWidth);
      fullSizeSrc = getPhotoUrl(keyValue, fullSize);
    }
  };

  if (!imgSrc) {
    getKeySource(record);
  } else if (typeof imgSrc === "object") {
    getKeySource(imgSrc);
  } else {
    fullSizeSrc = imgSrc;
  }

  // helper to render one image (or spinner)
  const renderImage = (imgSrc, fullSizeSrc, titleValue, status) => (
    <div>
      <img
        title={titleValue}
        alt={titleValue}
        src={imgSrc}
        lowsrc={fullSizeSrc}
        className={classes.image}
        {...sanitizeFieldRestProps(rest)}
      />
      {status === PhotoStatus.UPLOADING && (
        <div className={classes.spinnerContainer}>
          <CircularProgress color="inherit" />
        </div>
      )}
    </div>
  );

  if (Array.isArray(imgSrc)) {
    return (
      <ul
        className={classnames(classes.list, className)}
        {...sanitizeFieldRestProps(rest)}
      >
        {imgSrc.map((file, index) => {
          const fileTitleValue = get(file, title) || title;
          let srcValue = get(file, src) || title;
          let fullSizeSrc = null;

          if (!srcValue) {
            const keyValue = get(file, "key");
            if (keyValue) {
              srcValue = getPhotoUrl(keyValue, thumbWidth);
              fullSizeSrc = getPhotoUrl(keyValue, fullSize);
            }
          } else {
            fullSizeSrc = srcValue;
          }

          return (
            <li key={index}>
              {renderImage(srcValue, fullSizeSrc, fileTitleValue, status)}
            </li>
          );
        })}
      </ul>
    );
  }

  const titleValue = get(record, title) || title;

  return (
    <div className={className} {...sanitizeFieldRestProps(rest)}>
      {renderImage(imgSrc, fullSizeSrc, titleValue, status)}
    </div>
  );
};
